import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Target, CheckCircle2, AlertTriangle, Clock, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  active: { label: 'Aktif', color: 'text-primary', bg: 'border-primary/30 bg-primary/5', icon: Target },
  completed: { label: 'Tamamlandı', color: 'text-emerald-500', bg: 'border-emerald-500/30 bg-emerald-500/5', icon: CheckCircle2 },
  overdue: { label: 'Süresi geçti', color: 'text-red-500', bg: 'border-red-500/30 bg-red-500/5', icon: AlertTriangle },
};

/**
 * Yeni gelen kullanıcının zorunlu eğitim planı + 30/60/90 milestone takibi.
 * Plan yoksa (kullanıcı tenant'sız vs.) hiçbir şey render etmez.
 */
export default function OnboardingWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/user/me/onboarding`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        });
        if (r.ok) {
          const body = await r.json();
          if (!cancelled) setData(body);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data?.has_plan) return null;
  // Hiç zorunlu modül yoksa veya plan tamamlandıysa ve kullanıcı uzun süredir aktifse gizle
  if (data.target_count === 0) return null;

  const meta = STATUS_META[data.status] || STATUS_META.active;
  const Icon = meta.icon;

  return (
    <Card className={`border ${meta.bg}`} data-testid="onboarding-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className={`w-4 h-4 ${meta.color}`} />
          Zorunlu Eğitim Planın
          <Badge variant="outline" className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
          <span className="ml-auto text-xs flex items-center gap-1 text-muted-foreground">
            <Clock className="w-3 h-3" />
            {data.days_remaining} gün kaldı
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            {data.completed_count} / {data.target_count} modül tamamlandı
          </span>
          <span className={`font-bold ${meta.color}`}>%{data.completion_pct}</span>
        </div>
        <Progress value={data.completion_pct} className="h-2" />

        <div className="flex flex-wrap gap-1.5">
          {(data.target_modules || []).slice(0, 8).map(m => (
            <Badge
              key={m.id}
              variant="outline"
              className={`text-[10px] cursor-pointer hover:bg-primary/10 ${
                m.is_completed ? 'border-emerald-500/30 text-emerald-500' : ''
              }`}
              onClick={() => navigate(`/module/${m.id}`)}
              data-testid={`onboard-mod-${m.id}`}
            >
              {m.is_completed && <CheckCircle2 className="w-2.5 h-2.5 mr-1" />}
              {m.id}
            </Badge>
          ))}
          {(data.target_modules || []).length > 8 && (
            <span className="text-[10px] text-muted-foreground self-center">
              +{data.target_modules.length - 8}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border/40">
          <span className="text-[10px] text-muted-foreground">
            30/60/90 günlük milestone planı — son tarih:{' '}
            {new Date(data.deadline).toLocaleDateString('tr-TR')}
          </span>
          <Button size="sm" variant="ghost" onClick={() => navigate('/egitimler')} className="h-7 text-xs">
            Eğitimlere git <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
